import type { SurplusComponentTrace } from '@erp/shared-types';
import type { CompositionGroup } from './compositionDraft';

const GROUP_EMPTY_WARNINGS: Record<CompositionGroup, string> = {
  deduction: 'No deductions selected; commitments are not protected.',
  retention: 'No retained protection selected.',
  minimum: 'No minimum protection selected; minimum is configured zero.',
};

/** The shared-contract warning an empty group discloses in its own row. */
export function groupEmptyWarning(group: CompositionGroup): string {
  return GROUP_EMPTY_WARNINGS[group];
}

/**
 * Warnings that belong to the composition as a whole rather than to one
 * group; the per-group empties are shown inline where the gap is.
 */
export function sharedCompositionWarnings(warnings: string[]): string[] {
  const inline = new Set(Object.values(GROUP_EMPTY_WARNINGS));
  return warnings.filter((warning) => !inline.has(warning));
}

export function surplusQuantity(value: number | null | undefined): string {
  return value == null
    ? '—'
    : value.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

export function traceHasStateValue(row: SurplusComponentTrace): boolean {
  return ['not_selected', 'unavailable', 'not_evaluated'].includes(row.state);
}

export function traceIsDominated(
  row: SurplusComponentTrace,
  maximumKnown: boolean,
): boolean {
  if (!maximumKnown || row.dominant || row.quantity === null) return false;
  return ['minimum', 'retention', 'floor'].includes(row.group);
}

export function traceHasProvenance(row: SurplusComponentTrace): boolean {
  return [row.sourceAsOf, row.unit, row.source, row.providerId].some(Boolean);
}

export function retentionExpression(
  components: SurplusComponentTrace[],
  retained: number | null,
): string {
  const retention = components.filter(
    (component) => component.group === 'retention',
  );
  if (
    retention.length === 0 ||
    retention.some((component) => component.quantity === null)
  )
    return surplusQuantity(retained);
  return `max(${retention.map((component) => surplusQuantity(component.quantity)).join(', ')})`;
}

export function visibleCompositionWarnings(
  warnings: string[],
  minimumUsed: boolean,
): string[] {
  return warnings.filter(
    (warning) =>
      warning !==
        'Source stock quantity (unit unspecified); no conversion or cross-source comparison.' &&
      (minimumUsed ||
        warning !==
          'No minimum protection selected; minimum is configured zero.'),
  );
}

export function selectedTraceComponents(
  components: SurplusComponentTrace[],
): SurplusComponentTrace[] {
  return components.filter((component) => component.state !== 'not_selected');
}

export function minimumTraceComponents(
  components: SurplusComponentTrace[],
): SurplusComponentTrace[] {
  return components.filter((component) =>
    ['minimum', 'floor'].includes(component.group),
  );
}
